import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "../../store";
import {
  selectSelectedUser,
  selectUserLoading,
  selectUserError,
  fetchUserById,
  updateUser,
  deleteUser,
} from "../user/userSlice";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Skeleton,
  Stack,
  Badge,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Tooltip,
} from "@mui/material";
import {
  Edit,
  Delete,
  ArrowBack,
  Person,
  Email,
  Phone,
  LocationOn,
  Business,
  CalendarToday,
  Security,
  Assignment,
  MoreVert,
  Save,
  Cancel,
  Star,
  Work,
  TrendingUp,
  CheckCircle,
  Schedule,
  School,
  Verified,
  AccessTime,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { User, UserSkill, Branch, PerformanceMetrics } from "./user";
import UserQRCode from "../../components/UserQRCode";
import { config } from "../../config/environment";
import { UPLOAD_BASE_PATH } from "../../config/upload";
import { masterDropdownService } from "../../services/masterDropdownService";

const UserDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const theme = useTheme();

  const user = useAppSelector(selectSelectedUser);
  const loading = useAppSelector(selectUserLoading);
  const error = useAppSelector(selectUserError);

  const [editMode, setEditMode] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState<Partial<User>>({});
  const [roleOptions, setRoleOptions] = useState<Array<{ value: string; label: string }>>([]);
  // Deprecated: skillsService removed; no skills dropdown loading here

  useEffect(() => {
    console.log('UserDetails useEffect - ID from URL:', id);
    if (id) {
      console.log('Dispatching fetchUserById with ID:', id);
      dispatch(fetchUserById(id));
    }
  }, [dispatch, id]);

  // Fetch role types from API
  useEffect(() => {
    const fetchRoleTypes = async () => {
      try {
        const options = await masterDropdownService.getRoleTypesDropdown();
        // Options already have role_name as value and display_name as label from backend
        // Filter out super_admin from the options
        const filteredOptions = options.filter(option => 
          option.value !== 'super_admin' && option.value !== 'superadmin'
        );
        setRoleOptions(filteredOptions);
      } catch (error) {
        console.error('Failed to fetch role types:', error);
        // Fallback to default options if API fails (excluding super_admin)
        setRoleOptions([
          { value: "admin", label: "Admin" },
          { value: "branch_admin", label: "Branch Admin" },
          { value: "sales_person", label: "Sales Person" },
          { value: "supervisor", label: "Supervisor" },
          { value: "worker", label: "Operator" },
          { value: "manager", label: "Manager" },
        ]);
      }
    };
    
    fetchRoleTypes();
  }, []);

  function removeLastEndpoint(url) {
    // Remove trailing slash if present, then remove everything after the last "/"
    return url.replace(/\/+$/, "").substring(0, url.replace(/\/+$/, "").lastIndexOf("/"));
  }

  useEffect(() => {
    console.log(`url----- ${removeLastEndpoint(config.API_URL)}${UPLOAD_BASE_PATH}/${user?.avatarUrl}`);

  },[user])

  // No skills loading needed since skillsService is removed

  useEffect(() => {
    if (user) {
      setEditForm({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        phone: user.phone,
        role: user.role,
        status: user.status,
        skills: user.skills,
        department: user.department,
        employeeId: user.employeeId,
      });
    }
  }, [user]);

  const handleEdit = () => {
    setEditMode(true);
  };

  const handleSave = async () => {
    if (!user || !id) return;

    try {
      await dispatch(
        updateUser({ id, userData: editForm as any })
      ).unwrap();
      setEditMode(false);
    } catch (error) {
      // Error is already handled by the slice
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    if (user) {
      setEditForm({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        phone: user.phone,
        role: user.role,
        status: user.status,
        skills: user.skills,
        department: user.department,
        employeeId: user.employeeId,
      });
    }
  };

  const handleDelete = async () => {
    if (!user || !id) return;

    try {
      await dispatch(deleteUser(id)).unwrap();
      navigate("/users");
    } catch (error) {
      // Error is already handled by the slice
    }
  };

  const getStatusColor = (status: boolean) => {
    return status ? "success" : "error";
  };

  const getRoleColor = (role: string) => {
    const roleColors: { [key: string]: "primary" | "secondary" | "success" | "warning" | "error" | "info" } = {
      admin: "error",
      branch_admin: "warning",
      sales: "info",
      supervisor: "success",
      worker: "primary",
      management: "secondary",
    };
    return roleColors[role] || "default";
  };

  const getProficiencyColor = (level: string) => {
    const colors: { [key: string]: "success" | "warning" | "error" | "info" } = {
      beginner: "info",
      intermediate: "warning",
      advanced: "success",
      expert: "error",
    };
    return colors[level] || "default";
  };

  const getProficiencyValue = (level: string) => {
    const values: { [key: string]: number } = {
      beginner: 25,
      intermediate: 50,
      advanced: 75,
      expert: 100,
    };
    return values[level] || 0;
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Stack spacing={3}>
          <Skeleton variant="rectangular" height={60} />
          <Skeleton variant="rectangular" height={200} />
          <Skeleton variant="rectangular" height={300} />
        </Stack>
      </Box>
    );
  }

  if (!user) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">User not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
        <IconButton
          onClick={() => navigate("/users")}
          sx={{ color: "text.secondary" }}
        >
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" component="h1">
          User Details
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        {user?.id && user.id !== undefined ? (
          <UserQRCode
            userId={user.id}
            userName={`${user.firstName || ''} ${user.lastName || ''}`.trim()}
            userEmail={user.email}
            variant="button"
            size="medium"
            showProfileUrl={true}
          />
        ) : (
          <Button
            variant="outlined"
            disabled
            size="medium"
          >
            Loading User Data...
          </Button>
        )}
        {/* Debug info */}
        {console.log('UserDetails Debug:', { user, userId: user?.id })}
        {/* <Button
          variant="outlined"
          startIcon={<Edit />}
          onClick={handleEdit}
          disabled={editMode}
        >
          Edit
        </Button> */}
        {/* <IconButton
          color="error"
          onClick={() => setDeleteDialogOpen(true)}
          disabled={editMode}
        >
          <Delete />
        </IconButton> */}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      {user.performanceMetrics && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          {/* <Box sx={{ flex: { xs: '1 1 100%', sm: '0 0 calc(50% - 8px)', md: '0 0 calc(25% - 18px)' } }}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                  <TrendingUp color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h4" color="primary">
                    {user.performanceMetrics.startedItems}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Started Items
                </Typography>
              </CardContent>
            </Card>
          </Box> */}
          
          <Box sx={{ flex: { xs: '1 1 100%', sm: '0 0 calc(50% - 8px)', md: '0 0 calc(25% - 18px)' } }}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                  <CheckCircle color="success" sx={{ mr: 1 }} />
                  <Typography variant="h4" color="success.main">
                    {user.performanceMetrics.completedItems}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Completed Items
                </Typography>
              </CardContent>
            </Card>
          </Box>
          
          {/* <Box sx={{ flex: { xs: '1 1 100%', sm: '0 0 calc(50% - 8px)', md: '0 0 calc(25% - 18px)' } }}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                  <Schedule color="info" sx={{ mr: 1 }} />
                  <Typography variant="h4" color="info.main">
                    {user.performanceMetrics.inProgressItems}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  In Progress Items
                </Typography>
              </CardContent>
            </Card>
          </Box> */}
          
          <Box sx={{ flex: { xs: '1 1 100%', sm: '0 0 calc(50% - 8px)', md: '0 0 calc(25% - 18px)' } }}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                  <Assignment color="secondary" sx={{ mr: 1 }} />
                  <Typography variant="h4" color="secondary.main">
                    {user.performanceMetrics.totalActions}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Total Items
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}

      <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
        {/* User Profile Card */}
        <Box sx={{ flex: { xs: "1 1 100%", md: "0 0 350px" } }}>
          <Card>
            <CardContent sx={{ textAlign: "center", p: 3 }}>
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                badgeContent={
                  <Chip
                    label={user.status === "active" ? "Active" : "Inactive"}
                    size="small"
                    sx={{
                      fontSize: "0.7rem",
                      color: "#fff",
                      backgroundColor: user.status === "active" ? "success.main" : "error.main"
                    }}
                  />
                }
              >
                <Avatar
                src={user?.avatarUrl ? `${removeLastEndpoint(config.API_URL)}${UPLOAD_BASE_PATH}/${user.avatarUrl}` : undefined}
                sx={{ width: 120, height: 120, mx: "auto", mb: 2, fontSize: '3rem' }}>
                  {user.firstName?.[0] || ""}
                  {user.lastName?.[0] || ""}
                </Avatar>
              </Badge>

              <Typography variant="h5" gutterBottom>
                {user.firstName || ""} {user.lastName || ""}
              </Typography>

              <Chip
                label={user.role == "worker" ? "Operator" : user.role == 'sales_person' ? "Sales Person" : user.role}
                color={getRoleColor(user.role)}
                sx={{ mb: 2 }}
              />

              <Typography variant="body2" color="text.secondary" gutterBottom>
                {user.email}
              </Typography>

              {user.phone && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <Phone sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                  {user.phone}
                </Typography>
              )}

              {user.employeeId && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <Assignment sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                  ID: {user.employeeId}
                </Typography>
              )}

              {user.department && (
                <Typography variant="body2" color="text.secondary">
                  <Business sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                  {user.department}
                </Typography>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Branch Information */}
              {user.branch && (
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Branch Assignment
                  </Typography>
                  <Typography variant="body2" color="primary" gutterBottom>
                    {user.branch.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <LocationOn sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                    {user.branch.address}, {user.branch.city}, {user.branch.state}
                  </Typography>
                  <Chip 
                    label={user.branch.status} 
                    size="small" 
                    color={user.branch.status === 'active' ? 'success' : 'default'}
                    sx={{ mt: 1 }}
                  />
                </Box>
              )}

             
            </CardContent>
          </Card>
        </Box>

        {/* User Details and Skills */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Personal Information */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Personal Information
              </Typography>

              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Box
                  sx={{ flex: { xs: "1 1 100%", sm: "0 0 calc(50% - 8px)" } }}
                >
                  <TextField
                    label="First Name"
                    value={
                      editMode ? editForm.firstName || "" : user.firstName || ""
                    }
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        firstName: e.target.value,
                      }))
                    }
                    disabled={!editMode}
                    fullWidth
                    margin="normal"
                  />
                </Box>
                <Box
                  sx={{ flex: { xs: "1 1 100%", sm: "0 0 calc(50% - 8px)" } }}
                >
                  <TextField
                    label="Last Name"
                    value={
                      editMode ? editForm.lastName || "" : user.lastName || ""
                    }
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        lastName: e.target.value,
                      }))
                    }
                    disabled={!editMode}
                    fullWidth
                    margin="normal"
                  />
                </Box>
                <Box
                  sx={{ flex: { xs: "1 1 100%", sm: "0 0 calc(50% - 8px)" } }}
                >
                  <TextField
                    label="Email"
                    type="email"
                    value={editMode ? editForm.email || "" : user.email}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        email: e.target.value,
                      }))
                    }
                    disabled={!editMode}
                    fullWidth
                    margin="normal"
                  />
                </Box>
                <Box
                  sx={{ flex: { xs: "1 1 100%", sm: "0 0 calc(50% - 8px)" } }}
                >
                  <TextField
                    label="Phone"
                    value={editMode ? editForm.phone || "" : user.phone || ""}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        phone: e.target.value,
                      }))
                    }
                    disabled={!editMode}
                    fullWidth
                    margin="normal"
                  />
                </Box>
                <Box
                  sx={{ flex: { xs: "1 1 100%", sm: "0 0 calc(50% - 8px)" } }}
                >
                  <FormControl fullWidth margin="normal" disabled={!editMode}>
                    <InputLabel>Role</InputLabel>
                    <Select
                      value={editMode ? editForm.role || "" : user.role}
                      onChange={(e) => {
                        const newRole = e.target.value;
                        setEditForm((prev) => {
                          const updated = {
                            ...prev,
                            role: newRole,
                          };
                          // If role is changed to admin, clear the branch assignment
                          // Admins are common for all branches and should not have branch-specific assignment
                          if (newRole === "admin") {
                            updated.branchId = "";
                            console.log('Role changed to admin - clearing branch assignment');
                          }
                          return updated;
                        });
                      }}
                      label="Role"
                    >
                      {roleOptions.length > 0 ? (
                        roleOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))
                      ) : (
                        <>
                          <MenuItem value="admin">Admin</MenuItem>
                          <MenuItem value="branch_admin">Branch Admin</MenuItem>
                          <MenuItem value="sales_person">Sales Person</MenuItem>
                          <MenuItem value="supervisor">Supervisor</MenuItem>
                          <MenuItem value="worker">Operator</MenuItem>
                          <MenuItem value="manager">Manager</MenuItem>
                        </>
                      )}
                    </Select>
                  </FormControl>
                </Box>
                <Box
                  sx={{ flex: { xs: "1 1 100%", sm: "0 0 calc(50% - 8px)" } }}
                >
                  <FormControl fullWidth margin="normal" disabled={!editMode}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={
                        editMode
                          ? editForm.status
                            ? "active"
                            : "inactive"
                          : user.status
                          ? "active"
                          : "inactive"
                      }
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          status: e.target.value === "active",
                        }))
                      }
                      label="Status"
                    >
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              {editMode && (
                <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<Save />}
                    onClick={handleSave}
                  >
                    Save Changes
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Cancel />}
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Assigned Stages Section - Only show for workers */}
          {user.role === "worker" && user.userStages && user.userStages.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Assigned Stages
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {user.userStages.map((stage) => (
                    <Chip 
                      key={stage.id} 
                      label={`${stage.name}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  These are the workflow stages assigned to this worker. They can only complete these stages.
                </Typography>
              </CardContent>
            </Card>
          )}

          {/* Skills Section */}
          {user.userSkills && user.userSkills.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Skills & Expertise
                </Typography>
                
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Skill</TableCell>
                        <TableCell>Proficiency</TableCell>
                        <TableCell>Experience</TableCell>
                        <TableCell>Certification</TableCell>
                        <TableCell>Notes</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {user.userSkills.map((skill) => (
                        <TableRow key={skill.id}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {skill.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ minWidth: 120 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Chip 
                                  label={skill.proficiencyLevel} 
                                  size="small" 
                                  color={getProficiencyColor(skill.proficiencyLevel)}
                                />
                                <Typography variant="caption">
                                  {getProficiencyValue(skill.proficiencyLevel)}%
                                </Typography>
                              </Box>
                              <LinearProgress 
                                variant="determinate" 
                                value={getProficiencyValue(skill.proficiencyLevel)}
                                color={getProficiencyColor(skill.proficiencyLevel)}
                                sx={{ height: 6, borderRadius: 3 }}
                              />
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {skill.yearsExperience} years
                            </Typography>
                            {skill.acquiredDate && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                Since {new Date(skill.acquiredDate).toLocaleDateString()}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {skill.isCertified ? (
                              <Box>
                                <Chip 
                                  icon={<Verified />}
                                  label="Certified" 
                                  size="small" 
                                  color="success"
                                  sx={{ mb: 0.5 }}
                                />
                                {skill.certificationName && (
                                  <Typography variant="caption" display="block">
                                    {skill.certificationName}
                                  </Typography>
                                )}
                                {skill.certificationExpiry && (
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    Expires: {new Date(skill.certificationExpiry).toLocaleDateString()}
                                  </Typography>
                                )}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                Not certified
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {skill.notes ? (
                              <Tooltip title={skill.notes}>
                                <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                                  {skill.notes}
                                </Typography>
                              </Tooltip>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                —
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}
        </Box>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {user.firstName || ""}{" "}
            {user.lastName || ""}? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserDetails;
